/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Form,
    TextArea,
    Dropdown,
    Grid,
    Checkbox,
    Segment,
    Icon,
    GridColumn,
    FormField,
    FormGroup,
} from 'semantic-ui-react'
import { getProduct, saveItemPlatformPrices, saveProduct } from '../../../services/Product/Product.service'
import Uploader from '../../Uploader'
import ModalExtraFields from './ModalExtraFields'
import useUser from '../../Shared/UserProvider/useUser'
import { useTranslation } from 'react-i18next'
import { getPropertyValue, wrapValueInObject } from '../../../helpers/object'
import {
    isProviderAPos,
    setProviderItemInventory,
    setProviderItemSellingOption,
} from '../../../services/Provider/Provider.service'
import { isItemProvidedByUserProvider } from '../../../services/Item/Item.service'
import { useParams, useNavigate, Link } from "react-router-dom-v5-compat"
import { UltyInputText, UltyMoney } from '@ulty/ulty-ui'
import useBreadcrumb from '../../NavBar/useBreadcrumb'
import ItemAvailabilitySetter from '../../Items/ItemAvailabilitySetter'
import InventoryEdit from '../../Items/Inventory/InventoryEdit'
import { getCountryTaxRates } from '../../../services/Country/Country.service'
import PlatformPricesEdit from '../../Items/PlatformPrices/PlatformPricesEdit'
import { useQuery } from '@tanstack/react-query'
import { getMeasurementUnits } from '../../../services/MeasurementUnit/measurementUnit.service'
import { SellingOptionEdit } from '../../Items/sellingOption/SellingOptionEdit'
import useFeature from '../../Shared/FeatureProvider/useFeature'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'
import { CountriesOfOriginEdit } from '../../Items/CountriesOfOrigin/CountriesOfOriginEdit'
import { CountriesOfOriginLabel } from '../../Items/CountriesOfOrigin/CountriesOfOriginLabel'


const ProductEdit = () => {
    const {id} = useParams()
    const navigate = useNavigate()
    const [t] = useTranslation()
    const {user, can} = useUser()
    const {setPaths} = useBreadcrumb()
    const [loading, setLoading] = useState(false)
    const [canEditItem, setCanEditItem] = useState(false)
    const [taxRates, setTaxRates] = useState([])
    const [pristineProduct, setPristineProduct] = useState({})
    const [formState, setFormState] = useState({})
    const companyCurrency = user.getCompanyCurrency()

    const {isEnabled} = useFeature();
    const isDisplaySellingOptionsEnabled = useMemo(() => isEnabled('display_selling_options'),[isEnabled]);

    const [form, setForm] = useState({
        type: 'PRODUCT',
        basePrice: 0,
        taxRate: null,
        image: null,
        containsAlcohol: false,
        isUncountable: false,
        externalId: '',
        pricedByUnit: {
            type: 'COUNT',
        },
        providerIds: [],
        countriesOfOrigin: [],
        inventory: {
            quantity: 0,
            sellPrice: null,
            unavailableUntil: null,
            externalId: '',
            countriesOfOrigin: [],
        },
        object: {
            name: '',
            description: '',
            barcode: '',
            promotionalCategory: null,
        },
        platforms: [],
        sellingOption: null,
    })

    const [formErrors, setFormErrors] = useState({})

    useEffect(() => {
        (async function getTaxRates() {
            setTaxRates(await getCountryTaxRates(user.getUserCompany().country.id))
        })()
    }, [user])

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.catalog'),
            link: true,
            path: '/products',
        }, {
            text: form?.object?.name || t('global.loading'),
            link: true,
            path: `/products/${id}`,
        }, {
            text: t('breadcrumb.edition'),
            link: false,
        }])
    }, [form, id, t])

    useEffect(() => {
        if (!id) {
            setCanEditItem(true)
        }
    }, [id])

    const {data: product, isSuccess} = useQuery({
        queryKey: ['item', id],
        queryFn: () => getProduct(id),
        enabled: !!id,
    })

    useEffect(() => {
        if (isSuccess) {
            const keys = Object.keys(product.extraFields || {}), extra = {}
            keys.forEach(k => {
                if (k.indexOf('extra_') === 0) {
                    extra[k] = product.extraFields[k]
                }
            })

            setPristineProduct(product)

            setForm(prev => ({
                ...prev,
                id: product.id,
                type: product.type,
                providerIds: product.providers.map(p => p.id),
                basePrice: product.basePrice,
                pricedByUnit: product.pricedByUnit,
                taxRate: product.taxRate,
                image: product.images.find(image => image.isDefault),
                extraFields: extra,
                containsAlcohol: typeof product.containsAlcohol !== 'undefined' ? product.containsAlcohol : true,
                isUncountable: product.isUncountable,
                externalId: product.externalId,
                pricesByPlatform: product.pricesByPlatform,
                sellingOption: product.sellingOption || null,
                countriesOfOrigin: product.countriesOfOrigin || [],
                inventory: {
                    quantity: product.inventory?.quantity,
                    sellPrice: product.inventory?.sellPrice || null,
                    unavailableUntil: product.inventory?.unavailableUntil || null,
                    externalId: product.inventory?.externalId || '',
                    countriesOfOrigin: product.inventory?.countriesOfOrigin || [],
                },
                object: {
                    name: product.object.name,
                    barcode: product.object.barcode,
                    description: product.object.description,
                },
            }))

            setCanEditItem(isItemProvidedByUserProvider(product, user))
        }
    }, [isSuccess, product, user])

    const {data: measurementsUnits} = useQuery({
        queryKey: ['measurementsUnits'],
        queryFn: getMeasurementUnits,
    })

    const handleChangeItem = ({name, value, checked, type}) => {
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))

        setFormErrors(prev => ({
            ...prev,
            [name]: false,
        }))
    }

    const handleItemPlatformPriceChange = useCallback(({name, value, type}) => {

        const valueToSet = type === 'RESET' ? pristineProduct[name] : value
        setForm(prev => ({
            ...prev,
            [name]: valueToSet,
        }))

        setFormState(prev => ({
            ...prev,
            [name]: type === 'RESET' ? undefined : 'touched',
        }))

        setFormErrors(prev => ({
            ...prev,
            [name]: false,
        }))
    }, [setForm, setFormErrors, pristineProduct])

    const handleChangeObject = (_evt, {name, value, checked, type}) => {
        setForm(prev => ({
            ...prev,
            object: {
                ...prev.object,
                [name]: type === 'checkbox' ? checked : value,
            },
        }))

        setFormErrors(prev => ({
            ...prev,
            object: {
                ...prev.object,
                [name]: false,
            },
        }))
    }

    const handleChangeInventory = ({name, value, checked, type}) => {
        setForm(prev => ({
            ...prev,
            inventory: {
                ...prev.inventory,
                [name]: type === 'checkbox' ? checked : value,
            },
        }))
    }

    const handleChangeUncountable = ({name, value, checked, type}) => {
        handleChangeItem({name, value, checked, type})

        setForm(prev => ({
            ...prev,
            inventory: {
                ...prev.inventory,
                quantity: checked ? null : 0,
            },
        }))
    }

    const handleChangeExtraFields = (_evt, {name, value}) => {
        setForm(prev => ({
            ...prev,
            extraFields: {
                ...prev.object.extraFields,
                [name]: value,
            },
        }))
    }

    const checkErrors = () => {
        const mandatoryFields = ['basePrice', 'taxRate', 'object.name', 'object.barcode']

        let hasErrors = false, errorObject = {}
        mandatoryFields.forEach(mandatoryField => {
            const fieldValue = getPropertyValue(form, mandatoryField)
            if (!fieldValue || `${fieldValue}`.trim() === '') {
                errorObject = {
                    ...wrapValueInObject(formErrors, mandatoryField, true),
                    ...errorObject,
                }

                hasErrors = true
            }
        })
        setFormErrors(errorObject)
        return hasErrors
    }

    const handleSubmit = async () => {
        setLoading(true)

        const isError = checkErrors()
        if (isError) {
            ultyToast({ type: 'error', title: t('product_edit.missing_fields') })
            setLoading(false)
            return
        }

        try {
            let savedProduct = form
            if (can('UPDATE', 'product')) {
                savedProduct = await saveProduct(form)
                setForm(prev => ({
                    ...prev,
                    ...savedProduct,
                    providerIds: savedProduct.providers.map(p => p.id),
                }))
            }

            if (can('UPDATE', 'selling-option')) {
                await setProviderItemSellingOption(user.provider.id, savedProduct.id, form.sellingOption)
            }

            let inventory = null
            if (isProviderAPos(user.provider)) {
                inventory = await setProviderItemInventory(user.provider.id, savedProduct.id, {
                    sellPrice: form.inventory.sellPrice,
                    quantity: form.inventory.quantity,
                    externalId: form.inventory.externalId,
                    countriesOfOrigin: canEditItem ? undefined : form.inventory.countriesOfOrigin,
                })

                savedProduct.inventory = inventory
            }
            if (formState.pricesByPlatform) {
                let pricesByPlatform = await saveItemPlatformPrices(savedProduct.id, form.pricesByPlatform)
                savedProduct.pricesByPlatform = pricesByPlatform
            }

            ultyToast({ type: 'success', title: t('global.registerSuccess') })
            navigate(`/products/${savedProduct.id}`)
        } catch (e) {
            if (e.message === 'ean_already_exists') {
                ultyToast({ type: 'error', title: `${t(`product_edit.${e.message}`)}` })
            } else if (e.message === 'MIN_PERMITTED_GREATER_THAN_MAX_PERMITTED' || 'DEFAULT_QUANTITY_SHOULD_BE_BETWEEN_MIN_AND_MAX_PERMITTED') {
                ultyToast({ type: 'error', title: `${t(`item_edit.selling_option.errors.${e.message}`)}` })
            } else {
                ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
            }
        } finally {
            setLoading(false)
        }
    }

    const pricedByUnitUnit = form?.pricedByUnit?.type !== 'COUNT' ?
      form?.pricedByUnit?.unit :
      undefined

    const handleSellingOptionChange = (({name, value}) => {
        if (name === 'sellingOption') {
            setForm(prev => ({
                ...prev,
                sellingOption: value,
            }))
        } else if (form.sellingOption) {
            setForm(prev => ({
                ...prev,
                sellingOption: {
                    ...prev.sellingOption,
                    [name]: value,
                },
            }))
        }
    })

    return (
      <Segment>
          <Form>
              <Grid css={css`
                  .column {
                      padding-top: .5rem !important;
                      padding-bottom: .5rem !important;
                  }
              `}>
                  <GridColumn width={16}>
                      <div style={{textAlign: 'right', marginTop: '10px'}}>
                          <Button type="submit" color="teal" loading={loading} disabled={loading}
                                  onClick={handleSubmit}>{t('global.save')}</Button>
                      </div>
                  </GridColumn>

                  <GridColumn width={16}>
                      <UltyInputText
                        name="name"
                        readonly={!canEditItem}
                        label={t('item_edit.name')}
                        placeholder={t('item_edit.name')}
                        value={form.object.name}
                        onChange={(e) => handleChangeObject(e,{ name: 'name', value: e.target.value })}
                        error={formErrors?.object?.name || false}
                      />
                  </GridColumn>
                  <GridColumn width={8}>
                      <UltyInputText
                        name="barcode"
                        readonly={!canEditItem}
                        label={t('item_edit.ean')}
                        placeholder={t('item_edit.ean')}
                        value={form.object.barcode}
                        onChange={(e) => handleChangeObject(e,{ name: 'barcode', value: e.target.value })}
                        error={formErrors?.object?.barcode || false}
                      />
                  </GridColumn>
                  <GridColumn width={8}>
                      <UltyInputText
                        name="externalId"
                        readonly={!canEditItem}
                        value={form.externalId}
                        onChange={({target}) => handleChangeItem({name: target.name, value: target.value})}
                        label={t('item_edit.externalId')}
                        placeholder={t('item_edit.externalId')}
                      />
                  </GridColumn>
                  <GridColumn width={16}>
                      <FormField
                        control={TextArea}
                        name="description"
                        value={form.object.description}
                        disabled={!canEditItem}
                        error={formErrors?.object?.description || false}
                        onChange={handleChangeObject}
                        label={t('item_edit.description')}
                        placeholder={t('item_edit.description')}
                      />
                  </GridColumn>

                  <GridColumn tablet={8} mobile={16} computer={8} css={css`
                      display: flex !important;
                      align-items: center !important;
                      flex-direction: column !important;
                  `}>
                      <FormField css={css` width: 100%; `}>
                          <label>{t('item_edit.image')}</label>
                          <Uploader
                            disabled={!canEditItem}
                            initial={form.image?.url || ''}
                            withConversion={true}
                            onSrcChange={(url) => {
                                const image = url ? {url, isDefault: true} : null
                                handleChangeItem({name: 'image', value: image})
                            }}
                          />
                      </FormField>
                      {form.id && canEditItem &&
                        <Grid.Row>
                            <GridColumn textAlign="right">
                                <Link to={`/items/${form.id}/platforms`}><Icon
                                  name="settings"/>{t('item_edit.manageImages')}</Link>
                            </GridColumn>
                        </Grid.Row>}
                  </GridColumn>
                  <GridColumn tablet={8} mobile={16} computer={8}>
                      {!canEditItem && form.countriesOfOrigin?.length > 0 && !form.inventory.countriesOfOrigin?.length && (
                        <GridColumn width={16}>
                            <CountriesOfOriginLabel item={form} />
                        </GridColumn>
                      )}

                      {canEditItem && (
                        <CountriesOfOriginEdit
                          value={form.countriesOfOrigin}
                          onChange={(value) => handleChangeItem({ name: 'countriesOfOrigin', value })}
                        />
                      )}

                      <FormGroup widths={2}>
                          <FormField>
                              <UltyMoney
                                readonly={!canEditItem}
                                name="basePrice"
                                label={t('item_edit.basePrice')}
                                perUnitOption={pricedByUnitUnit ? {
                                    value: pricedByUnitUnit,
                                    text: t(`measurement_unit.symbols.${pricedByUnitUnit}`),
                                } : {value: companyCurrency, text: ''}}
                                perUnitOptions={!isDisplaySellingOptionsEnabled ? [] : (measurementsUnits || [])
                                  .filter(measurementsUnit =>
                                    measurementsUnit.type !== 'COUNT'
                                    && measurementsUnit.type !== 'VOLUME'
                                    && (measurementsUnit.type === 'WEIGHT' && measurementsUnit.unit !== 'METRIC_MILLIGRAM'),
                                  )
                                  .map(measurementsUnit => ({
                                      key: measurementsUnit.id,
                                      value: measurementsUnit.unit,
                                      text: t(`measurement_unit.symbols.${measurementsUnit.unit}`),
                                  }))
                                  .concat([{key: companyCurrency, value: companyCurrency, text: ''}])
                                }
                                onChange={(value) => {
                                    const {perUnitOption} = value
                                    const measurementUnit = (perUnitOption.value === companyCurrency ?
                                      measurementsUnits.find(measurementUnit => measurementUnit.type === 'COUNT') :
                                      measurementsUnits.find(measurementUnit => measurementUnit.unit === perUnitOption.value))

                                    if (!measurementUnit) {
                                        return
                                    }

                                    handleSellingOptionChange({
                                        name: 'sellingOption',
                                        value: measurementUnit.type === 'COUNT' ? null : {
                                            measurementUnit: measurementsUnits?.[0],
                                            conversionRate: 1,
                                            constraints: {
                                                minPermitted: null,
                                                maxPermitted: null,
                                                incrementBy: null,
                                                defaultQuantity: null,
                                            },
                                        },
                                    })

                                    handleChangeItem({
                                        name: 'pricedByUnit',
                                        value: {
                                            id: measurementUnit.id,
                                            type: measurementUnit.type,
                                            unit: measurementUnit.unit,
                                        },
                                    })
                                }}
                                amount={form.basePrice}
                                onMoneyAmountChange={(e, amt) => handleChangeItem({name: e.target.name, value: amt})}
                                currencyCode={user.getCompanyCurrency()}
                                locale={user.getCompanyLocale()}
                              />
                          </FormField>

                          <FormField>
                              <label>{t('item_edit.taxRate')}</label>
                              <Dropdown
                                placeholder={t('item_edit.taxRate')}
                                fluid
                                selection
                                name="taxRate"
                                value={form.taxRate}
                                disabled={!canEditItem}
                                error={formErrors.taxRate || false}
                                onChange={(e, {name, value}) => handleChangeItem({name, value})}
                                options={taxRates.map(taxRate => ({
                                    text: `${taxRate}%`,
                                    value: taxRate,
                                    key: taxRate,
                                }))}
                              />
                          </FormField>
                      </FormGroup>

                      <Form.Group widths={2}>
                          <FormField>
                              <label>&nbsp;</label>
                              <Checkbox
                                label={t('item_edit.contains_alcohol')}
                                disabled={!canEditItem}
                                checked={form.containsAlcohol}
                                onChange={(e, {name, value, type, checked}) =>
                                  handleChangeItem({name, value, type, checked})}
                                name="containsAlcohol"
                              />
                          </FormField>
                          <FormField>
                              <label>&nbsp;</label>
                              <Checkbox
                                label={t('item_edit.is_uncountable')}
                                disabled={!canEditItem}
                                checked={form.isUncountable}
                                onChange={(_e, {name, value, type, checked}) => {
                                    handleChangeUncountable({name, value, checked, type})
                                }}
                                name="isUncountable"
                              />
                          </FormField>
                      </Form.Group>
                      {!isProviderAPos(user.provider) && (
                        <>
                            <PlatformPricesEdit
                              isLoading={loading}
                              basePrice={form.basePrice}
                              pricedByUnit={form.pricedByUnit}
                              pricesByPlatform={form.pricesByPlatform}
                              onChange={handleItemPlatformPriceChange.bind(this)}
                            />
                        </>
                      )}

                      {isProviderAPos(user.provider) &&
                        <>
                            <InventoryEdit
                              type={form.type}
                              inventory={form.inventory}
                              isUncountable={form.isUncountable}
                              isLoading={loading}
                              basePrice={form.basePrice}
                              pricedByUnit={form.pricedByUnit}
                              pricesByPlatform={form.pricesByPlatform}
                              showCountriesOfOrigin={!canEditItem}
                              onChange={({
                                             name,
                                             value,
                                             type,
                                         }) => name === 'pricesByPlatform' ? handleItemPlatformPriceChange({
                                  name,
                                  value,
                                  type,
                              }) : handleChangeInventory({name, value})}
                            />

                            {id &&
                              <ItemAvailabilitySetter
                                css={css` margin-top: 1rem; `}
                                itemId={id}
                                onChange={(unavailableUntil) => handleChangeInventory({
                                    name: 'unavailableUntil',
                                    value: unavailableUntil,
                                })}/>
                            }
                        </>
                      }

                      <SellingOptionEdit
                        pricedByUnit={form.pricedByUnit}
                        sellingOption={form.sellingOption}
                        showSellingOptions={form.sellingOption !== null}
                        isEditable={canEditItem}
                        onChange={handleSellingOptionChange}
                      />
                  </GridColumn>
                  {isProviderAPos(user.provider) &&
                    <ModalExtraFields extraFields={form.extraFields} handleChange={handleChangeExtraFields}/>}
                  <GridColumn width={16}>
                      <div style={{textAlign: 'right', marginTop: '10px'}}>
                          <Button type="submit" color="teal" loading={loading} disabled={loading}
                                  onClick={handleSubmit}>{t('global.save')}</Button>
                      </div>
                  </GridColumn>
              </Grid>
          </Form>
      </Segment>
    )
}

ProductEdit.propTypes = {
    merchant: PropTypes.object,
}

export default ProductEdit
